import * as React from 'react';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';
import { Col, Row } from 'antd';

import { DataLocalService } from 'src/core/services/data-local.service';
import { ObjectUtils } from 'src/core/utils/object';
import { ToastNotifications } from 'src/core/ui/toast';
import { TaxonomyService } from 'src/app/bookings/shared/taxonomy.service';
import { DataTableView, Column, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { CardController } from 'src/app/shared/card.controller';

interface Props {
	entityName: string;
	model: {
		dynamicProperties: { [prop: string]: any };
	};
	mode: ControlMode;
	setBusy: (isBusy: boolean) => void;
}

interface State {
	mappedProperties: PropertySummary[];
	availableFields: TaxonomicField[];
}

@observer
export class TaxonomicFieldList extends React.Component<Props, State> {
	@resolve
	private taxonomyService: TaxonomyService;

	@resolve
	private toast: ToastNotifications;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			availableFields: [],
			mappedProperties: []
		};
	}

	componentDidMount(): void {
		this.props.setBusy(true);

		this.loadData()
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	componentWillReceiveProps(nextProps: Readonly<Props>): void {
		this.mapProperties(nextProps);
	}

	private async loadData(): Promise<void> {
		const { entityName } = this.props;
		const availableFields = await this.taxonomyService.getEntityTaxonomies(entityName);

		this.setState({
			availableFields
		});

		this.mapProperties();
	}

	private mapProperties(props?: Readonly<Props>): void {
		const { model } = props || this.props;
		const properties = model.dynamicProperties['taxonomicFields'] || [];
		const { availableFields } = this.state;

		const mappedProperties = [];
		for (let property of properties) {
			const field = availableFields.find(m => m.jsonSchemaPropertyName === property);

			if (field != null) {
				mappedProperties.push({
					propertyName: property,
					displayName: field == null ? null : field.displayName
				});
			}
		}

		this.setState({
			mappedProperties
		});
	}

	isValidItem = (property: PropertySummary): Promise<React.ReactNode[]> => {
		const { availableFields } = this.state;

		if (availableFields.some(m => m.jsonSchemaPropertyName === property.propertyName)) {
			return Promise.resolve([]);
		}

		return Promise.resolve([<p>Properties can't be null</p>]);
	};

	handleChangeProperties = (properties: PropertySummary[]): void => {
		const { model } = this.props;
		model.dynamicProperties['taxonomicFields'] = properties.map(m => m.propertyName);

		this.setState({
			mappedProperties: properties
		});
	};

	handleAddProperty = (): Promise<PropertySummary> => {
		const { mappedProperties } = this.state;
		const newProperty: PropertySummary = {
			displayName: '',
			propertyName: null
		};

		this.handleChangeProperties([...mappedProperties, newProperty]);
		return Promise.resolve(newProperty);
	};

	render(): React.ReactNode {
		const { mode, setBusy } = this.props;
		const { availableFields, mappedProperties } = this.state;
		const model = new DataLocalService<any>(mappedProperties);

		const columns: Column[] = [
			{
				title: 'Property',
				key: 'propertyName',
				dataIndex: 'propertyName',
				editable: (field: string, record: PropertySummary, index: number, isSelected: boolean) => (
					!isSelected
						? record.displayName
						: (
							<DropdownView
								required
								label={null}
								options={availableFields.filter(m => m.jsonSchemaPropertyName === field || mappedProperties.every(p => p.propertyName !== m.jsonSchemaPropertyName))}
								model={field}
								onChanged={(newField) => {
									if (newField == null) {
										record.displayName = '';
										record.propertyName = null;
									} else {
										record.displayName = newField.displayName;
										record.propertyName = newField.jsonSchemaPropertyName;
									}

									this.handleChangeProperties([...mappedProperties]);
								}}
								keyField="jsonSchemaPropertyName"
								textField="displayName"
							/>
						)
				)
			}
		];

		return (
			<CardController
				mode={mode}
				icon="book"
				className="card-borderless"
				title="Taxonomies for its Products">
				<Row gutter={16}>
					<Col span={24}>
						<DataTableView
							hideToolbar={ToolbarVisibility.hideSearch}
							mode={mode}
							rowKey={(m) => m.propertyName || ''}
							model={model}
							isValidItem={this.isValidItem}
							createNewItem={this.handleAddProperty}
							columns={columns}
							setBusy={setBusy}
						/>
					</Col>
				</Row>
			</CardController>
		);
	}
}

interface PropertySummary {
	propertyName: string;
	displayName: string;
}



// WEBPACK FOOTER //
// ./src/app/products/product-categories/form/parts/taxonomic-field-list.tsx